import { motion } from "framer-motion";
import { Boxes, Gauge } from "lucide-react";
import SectionLabel from "@/components/sections/SectionLabel";

const functional = [
  "User journeys",
  "Login",
  "Signup",
  "Checkout",
  "Forms",
  "Navigation",
  "API correctness",
  "Permissions",
  "Business rules",
  "Regression testing",
];

const nonFunctional = [
  "Visual regressions",
  "Responsive UI",
  "Layout shifts",
  "Accessibility",
  "Performance",
  "Loading states",
  "Broken assets",
  "Slow interactions",
  "Infinite loading",
  "Console errors",
  "Memory leaks",
  "Network failures",
];

export default function TestingTypes() {
  return (
    <section
      id="capabilities"
      data-testid="testing-types-section"
      className="relative py-24 md:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <SectionLabel>Coverage</SectionLabel>
          <h2 className="font-display font-semibold mt-4 text-balance text-4xl sm:text-5xl md:text-[56px] leading-[1.05] tracking-[-0.025em]">
            Functional and non-functional —{" "}
            <span className="text-muted-foreground">in a single run.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-14">
          <CategoryCard
            testid="testing-functional-card"
            icon={Boxes}
            kicker="Functional"
            title="Does the product work?"
            items={functional}
          />
          <CategoryCard
            testid="testing-non-functional-card"
            icon={Gauge}
            kicker="Non-Functional"
            title="Does it feel right at scale?"
            items={nonFunctional}
          />
        </div>
      </div>
    </section>
  );
}

function CategoryCard({ testid, icon: Icon, kicker, title, items }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="premium-card p-7 md:p-10 relative overflow-hidden"
    >
      <div className="flex items-center justify-between mb-8">
        <span className="mono-label text-[hsl(var(--accent))]">{kicker}</span>
        <div className="h-11 w-11 rounded-xl bg-foreground/[0.04] border border-border text-foreground/80 inline-flex items-center justify-center">
          <Icon className="h-5 w-5" strokeWidth={1.6} />
        </div>
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">
        {title}
      </h3>

      <div className="mt-8 flex flex-wrap gap-2">
        {items.map((i, idx) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: 0.02 * idx }}
            className="px-3 py-1.5 text-[13px] rounded-full border border-border bg-background/40 hover:border-border-strong hover:text-foreground transition-colors text-foreground/75"
          >
            {i}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
}
